<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="queue-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('queue.title') }}</h1>
          <p class="page-subtitle">{{ $t('queue.subtitle') }}</p>
        </div>
      </div>

      <!-- Queue Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="queueItems"
          :loading="isLoading"
          hoverable
        >
          <template #cell-submitted_at="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-sla="{ row }">
            <span :class="{ 'sla-overdue': isOverdue(row.submitted_at) }">
              {{ getSlaText(row.submitted_at) }}
            </span>
          </template>
          <template #cell-actions="{ row }">
            <IntercomButton variant="primary" size="sm" @click="openAssignModal(row)">
              {{ $t('queue.assign') }}
            </IntercomButton>
          </template>
        </IntercomTable>
        <p v-if="!isLoading && queueItems.length === 0" class="empty-text">
          {{ $t('queue.emptyQueue') }}
        </p>
      </IntercomCard>

      <!-- Scholar Assignment Modal -->
      <IntercomModal
        v-model:show="showAssignModal"
        :title="$t('queue.assignScholar')"
      >
        <div class="assign-modal-body">
          <p class="assign-case-info">
            {{ $t('queue.assigningCase') }}:
            <strong>{{ selectedCase?.case_number }}</strong> — {{ selectedCase?.deceased_name }}
          </p>

          <!-- Scholar Selection -->
          <div class="scholar-select">
            <label class="field-label">{{ $t('queue.selectScholar') }}</label>
            <div v-if="scholarsLoading" class="loading-text">{{ $t('common.loading') }}</div>
            <div v-else class="scholar-list">
              <label
                v-for="s in activeScholars"
                :key="s.id"
                class="scholar-option"
                :class="{ selected: selectedScholarId === s.id }"
              >
                <input type="radio" :value="s.id" v-model="selectedScholarId" class="scholar-radio" />
                <div class="scholar-info">
                  <span class="scholar-name">{{ s.name }}</span>
                  <span class="scholar-meta">
                    {{ s.school_specialization }} · {{ s.cases_reviewed_count }} {{ $t('queue.reviewed') }}
                  </span>
                </div>
              </label>
              <p v-if="activeScholars.length === 0" class="no-scholars">
                {{ $t('scholar.noScholars') }}
              </p>
            </div>
          </div>
        </div>

        <template #footer>
          <div class="modal-footer">
            <IntercomButton variant="ghost" @click="showAssignModal = false">
              {{ $t('common.cancel') }}
            </IntercomButton>
            <IntercomButton
              variant="primary"
              :disabled="!selectedScholarId"
              :loading="isAssigning"
              @click="handleAssignAndGenerateLink"
            >
              {{ $t('queue.assignAndSendLink') }}
            </IntercomButton>
          </div>
        </template>
      </IntercomModal>

      <!-- Review Link Modal -->
      <IntercomModal
        v-model:show="showLinkModal"
        :title="$t('queue.reviewLinkReady')"
      >
        <div class="link-modal-body">
          <p class="link-success-text">{{ $t('queue.linkGenerated') }}</p>

          <div class="link-box">
            <input
              type="text"
              :value="reviewLinkUrl"
              readonly
              class="link-input"
              ref="linkInput"
            />
            <IntercomButton variant="ghost" size="sm" @click="copyLink">
              {{ copied ? $t('queue.copied') : $t('queue.copyLink') }}
            </IntercomButton>
          </div>

          <a
            v-if="reviewLinkScholarPhone"
            :href="whatsappLink"
            target="_blank"
            class="whatsapp-link"
          >
            {{ $t('queue.sendViaWhatsApp') }} ({{ reviewLinkScholarName }})
          </a>

          <p class="link-expiry">
            {{ $t('queue.linkExpires') }}: {{ reviewLinkExpiry }}
          </p>
        </div>

        <template #footer>
          <IntercomButton variant="primary" @click="showLinkModal = false">
            {{ $t('common.close') }}
          </IntercomButton>
        </template>
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomModal,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { getScholarQueue, listScholars, pickupCase, generateReviewLink } = useFaraid()
const { navSections } = useNavigation()

const queueItems = ref([])
const isLoading = ref(false)

// Scholar assignment
const showAssignModal = ref(false)
const selectedCase = ref(null)
const activeScholars = ref([])
const selectedScholarId = ref(null)
const scholarsLoading = ref(false)
const isAssigning = ref(false)

// Review link
const showLinkModal = ref(false)
const reviewLinkUrl = ref('')
const reviewLinkScholarName = ref('')
const reviewLinkScholarPhone = ref('')
const reviewLinkExpiry = ref('')
const copied = ref(false)

// Default SLA: 48 hours
const SLA_HOURS = 48

const columns = [
  { key: 'case_number', title: t('queue.caseNumber'), width: '130px' },
  { key: 'deceased_name', title: t('queue.deceased'), width: '160px' },
  { key: 'school', title: t('queue.school'), width: '100px' },
  { key: 'heir_count', title: t('queue.heirs'), width: '70px', align: 'center' },
  { key: 'submitted_at', title: t('queue.submitted'), width: '120px' },
  { key: 'sla', title: t('queue.sla'), width: '120px' },
  { key: 'actions', title: '', width: '100px' },
]

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const isOverdue = (submittedAt) => {
  if (!submittedAt) return false
  return dayjs().diff(dayjs(submittedAt), 'hour') > SLA_HOURS
}

const getSlaText = (submittedAt) => {
  if (!submittedAt) return ''
  const hoursElapsed = dayjs().diff(dayjs(submittedAt), 'hour')
  const remaining = SLA_HOURS - hoursElapsed
  if (remaining <= 0) return t('queue.overdue')
  if (remaining < 24) return `${remaining}h left`
  return `${Math.floor(remaining / 24)}d ${remaining % 24}h left`
}

const whatsappLink = computed(() => {
  if (!reviewLinkScholarPhone || !reviewLinkUrl.value) return '#'
  const phone = reviewLinkScholarPhone.value.replace(/\D/g, '')
  const text = encodeURIComponent(
    `Assalamu Alaikum,\n\nYou have been assigned a Faraid case for review.\n\nPlease use this link to review and submit your decision:\n${reviewLinkUrl.value}\n\nThis link is valid for 7 days.`
  )
  return `https://wa.me/${phone}?text=${text}`
})

const fetchQueue = async () => {
  isLoading.value = true
  try {
    const response = await getScholarQueue()
    queueItems.value = response.queue || []
  } catch (error) {
    console.error('Failed to fetch queue:', error)
  } finally {
    isLoading.value = false
  }
}

const fetchScholars = async () => {
  scholarsLoading.value = true
  try {
    const response = await listScholars({ active: 'true', limit: 50 })
    activeScholars.value = (response.data || response.items || []).filter(s => s.is_active)
  } catch (error) {
    console.error('Failed to fetch scholars:', error)
  } finally {
    scholarsLoading.value = false
  }
}

const openAssignModal = (item) => {
  selectedCase.value = item
  selectedScholarId.value = null
  showAssignModal.value = true
  if (activeScholars.value.length === 0) {
    fetchScholars()
  }
}

const handleAssignAndGenerateLink = async () => {
  if (!selectedScholarId.value || !selectedCase.value) return

  isAssigning.value = true
  try {
    // Step 1: Assign scholar to case (pickup)
    await pickupCase(selectedCase.value.id, { scholar_id: selectedScholarId.value })
    toast.success(t('success.casePickedUp'))

    // Step 2: Generate review link
    const linkResponse = await generateReviewLink(selectedCase.value.id)
    const linkData = linkResponse.data || linkResponse

    reviewLinkUrl.value = linkData.url
    reviewLinkScholarName.value = linkData.scholar_name
    reviewLinkScholarPhone.value = linkData.scholar_phone || ''
    reviewLinkExpiry.value = linkData.expires_at
      ? dayjs(linkData.expires_at).format('DD MMM YYYY, h:mm A')
      : '7 days'

    showAssignModal.value = false
    showLinkModal.value = true

    // Refresh queue (assigned case should disappear)
    fetchQueue()
  } catch (error) {
    toast.error(error.response?.data?.detail || error.message)
  } finally {
    isAssigning.value = false
  }
}

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(reviewLinkUrl.value)
    copied.value = true
    toast.success(t('queue.linkCopied'))
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    // Fallback for older browsers
    const input = document.querySelector('.link-input')
    if (input) {
      input.select()
      document.execCommand('copy')
      copied.value = true
      setTimeout(() => { copied.value = false }, 2000)
    }
  }
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchQueue()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.queue-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.sla-overdue {
  color: var(--color-error);
  font-weight: var(--font-weight-semibold);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

/* Assign Modal */
.assign-modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.assign-case-info {
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  margin: 0;
}

.field-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-600);
  margin-bottom: var(--space-2);
  display: block;
}

.loading-text {
  font-size: var(--font-size-sm);
  color: var(--color-gray-500);
  padding: var(--space-4);
}

.scholar-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-height: 300px;
  overflow-y: auto;
}

.scholar-option {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.15s ease;
}

.scholar-option:hover {
  border-color: var(--color-blue);
}

.scholar-option.selected {
  border-color: var(--color-blue);
  background: #eff6ff;
}

.scholar-radio {
  margin: 0;
}

.scholar-info {
  display: flex;
  flex-direction: column;
}

.scholar-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  color: var(--color-charcoal);
}

.scholar-meta {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
}

.no-scholars {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-4);
  margin: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}

/* Link Modal */
.link-modal-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.link-success-text {
  color: #059669;
  font-weight: var(--font-weight-medium);
  margin: 0;
}

.link-box {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.link-input {
  flex: 1;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  color: var(--color-charcoal);
  background: var(--color-gray-50);
}

.whatsapp-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: #25d366;
  color: white;
  border-radius: var(--radius-md);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  text-align: center;
  justify-content: center;
}

.whatsapp-link:hover {
  background: #1fb855;
}

.link-expiry {
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  margin: 0;
}
</style>
